import { LosseLink, LossePlaatjie, useLoaderData } from '@ubo/losse-sjedel'
import Animated from '~/components/elements/Animated'
import Content from '~/components/elements/Content'
import CopyButton from '~/components/elements/CopyButton'
import type { Page_Flexcontent_Flex_Hubspot, RootQueryToExpertConnection } from '~/graphql/types'
import { type ProfilePageLoaderData } from '~/services/hubspot.server'
import { phases } from './HubspotBanner'
import clsx from 'clsx'

type Props = Page_Flexcontent_Flex_Hubspot & {
  subdata?: {
    Advisors: RootQueryToExpertConnection
  }
}

export default function HubspotAdvisor({ fields }: { fields: Props }) {
  const { hubspotData: profile } = useLoaderData<ProfilePageLoaderData>()
  const advisor = fields.subdata?.Advisors?.nodes.find(
    (advisor) =>
      advisor.title?.toLowerCase() === `${profile.owner.firstName} ${profile.owner.lastName}`.toLowerCase() ||
      advisor.recap.email?.toLowerCase() === profile.owner.email?.toLowerCase()
  )
  const currentPhase = phases.find((phase) => phase.id.some((id) => String(id) === profile.deals[0].properties.dealstage))

  if (!advisor) return null

  return (
    <section className="section--large" data-component="HubspotAdvisor">
      <div className="container">
        <div className="max-w-[1000px] mx-auto flex md:flex-row flex-col gap-y-10 gap-x-16 lg:gap-x-28">
          <div className={clsx(currentPhase.step === 1 && 'items-center', 'flex max-md:justify-center')}>
            <div className="relative max-w-[295px] max-h-[381px]">
              <Animated delay={100}>
                <div className="bg-black pb-9">
                  <LossePlaatjie
                    className="min-w-[295px] w-[295px] h-[345px] object-cover"
                    maxwidth={1000}
                    src={advisor.featuredImage.node}
                  />
                </div>
              </Animated>

              <Animated delay={300}>
                <div className="absolute bottom-0 left-0 w-full pb-5">
                  <div className="absolute w-full h-full left-0 top-0 bg-gradient-to-t from-black via-black to-transparent" />
                  <span className="block relative z-20 mb-1 text-white font-bold text-center text-2xl">{advisor.title}</span>
                  <span className="block relative z-20 mb-1 text-site-accent text-center">Adviseur</span>
                  <span className="block relative z-20 text-site-accent text-center">{advisor.recap.functiongroup?.[0]?.name}</span>
                </div>
              </Animated>
            </div>
          </div>
          <div className="w-full flex flex-col max-md:items-center justify-center">
            <Animated delay={150}>
              <h2 className="title--medium !leading-[1.2] max-md:text-center mb-4">
                {fields.title.replace('{firstname}', advisor.recap.firstname)}
              </h2>
            </Animated>
            <Animated delay={200}>
              <div>
                {fields.description?.includes('{facts}') && (
                  <div className="flex flex-col pl-4 gap-y-5 mb-6">
                    {advisor.recap.facts.map((fact, index) => (
                      <div key={index} className="flex gap-x-7">
                        <div className="min-w-5 w-5 h-[3px] mt-[10px] flex items-center justify-center bg-site-accent" />
                        <span className="text-site-base">{fact.fact}</span>
                      </div>
                    ))}
                  </div>
                )}
                <Content className="max-w-[485px] max-md:text-center">
                  {fields.description
                    ?.replaceAll('{firstname}', advisor.recap.firstname)
                    ?.replaceAll('{lastname}', profile.owner.lastName)
                    ?.replace('{personalpageStart}', `<a target="_blank" href="${advisor.uri}">`)
                    ?.replace('{personalpageEnd}', '</a>')
                    ?.replace('{facts}', '')}
                </Content>
              </div>
            </Animated>
            <Animated delay={300}>
              <div className="max-w-[400px] grid grid-cols-2 gap-y-5 gap-x-3 mt-8">
                <div className="col-span-2">
                  <LosseLink className="btn--accent w-full" target={advisor.recap.whatsapp.target} to={advisor.recap.whatsapp.url}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M4.08047 19.8762L5.26642 15.679C4.5045 14.388 4.10698 12.9184 4.10698 11.409C4.10698 6.76158 7.89007 2.98145 12.5411 2.98145C17.1921 2.98145 20.9752 6.76158 20.9752 11.409C20.9752 16.0563 17.1921 19.8365 12.5411 19.8365C11.0901 19.8365 9.6723 19.4657 8.41348 18.764L4.08047 19.8762ZM8.63874 17.2281L8.89713 17.387C9.99032 18.0556 11.2491 18.4065 12.5411 18.4065C16.4037 18.4065 19.5441 15.2685 19.5441 11.409C19.5441 7.54938 16.4037 4.41141 12.5411 4.41141C8.67849 4.41141 5.53806 7.54938 5.53806 11.409C5.53806 12.7529 5.92233 14.057 6.6445 15.1825L6.81676 15.4539L6.13434 17.8703L8.63874 17.2281Z"
                        fill="white"
                      />
                      <path
                        d="M10.0387 6.99434L9.4643 6.95728C9.28437 6.94987 9.11135 7.00917 8.97294 7.13517C8.69612 7.39459 8.2532 7.8912 8.11479 8.54347C7.91409 9.51445 8.22552 10.7004 9.03523 11.8863C9.84494 13.0722 11.3605 14.9697 14.0388 15.7777C14.9039 16.0371 15.5821 15.8592 16.1012 15.5034C16.5164 15.2217 16.8001 14.7622 16.9039 14.2508L16.9939 13.7912C17.0216 13.643 16.9524 13.5021 16.8278 13.4354L14.9039 12.4867C14.7793 12.4274 14.6271 12.4645 14.544 12.583L13.7828 13.6356C13.7274 13.7171 13.6305 13.7467 13.5405 13.7097C13.0215 13.517 11.2775 12.7313 10.3225 10.7597C10.2809 10.6707 10.2879 10.567 10.3501 10.5003L11.0768 9.6034C11.1529 9.51445 11.1668 9.38103 11.1252 9.26985L10.2948 7.17965C10.2533 7.07588 10.1494 7.00176 10.0387 6.99434Z"
                        fill="#EAEAEB"
                      />
                    </svg>
                    {advisor.recap.whatsapp.title}
                  </LosseLink>
                </div>
                {currentPhase.step === 1 && (
                  <>
                    <div>
                      <CopyButton
                        className="btn--outline--black w-full max-xs:text-sm"
                        value={<>Bel met {advisor.recap.firstname}</>}
                        hiddenValue={advisor.recap.phone}
                        mobileHiddenValue={`tel:${advisor.recap.phone}`}
                      />
                    </div>
                    <div>
                      <CopyButton
                        className="btn--outline--black w-full max-xs:text-sm"
                        value={<>Mail naar {advisor.recap.firstname}</>}
                        hiddenValue={advisor.recap.email}
                        mobileHiddenValue={`tel:${advisor.recap.email}`}
                      />
                    </div>
                  </>
                )}
              </div>
            </Animated>
          </div>
        </div>
      </div>
    </section>
  )
}
